/* eslint-disable import/no-unresolved */
import React, { useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import styled from "styled-components"
import tw from "twin.macro"

import { srConfig } from "@config"
import { SkillTree } from "@components"
import { Dots } from "@components/icons"
import { CustomHeading } from "@templates"
import scrollReveal from "@utils/scroll.reveal"

const StyledAbout = styled.section`
  ${tw`min-h-full text-suvaGrey flex flex-col justify-center mb-12`}
  & {
    .custom-heading {
      ${tw`px-8`}
    }
  }
`

const StyledContent = styled.div`
  ${tw`md:px-8 md:flex md:flex-row-reverse md:items-center md:gap-10 lg:gap-20`}
  & {
    .text-about {
      ${tw`px-8 md:px-0 md:w-2/3`}
      .first {
        ${tw`text-suvaGrey`}
        p {
          ${tw`md:w-11/12`}
          :not(:first-child) {
            line-height: 1.5;
          }
        }
        .upper-text {
          ${tw`text-suvaGrey`}
        }
      }
      .second {
        ${tw`mt-8`}
        h1 {
          ${tw`font-semibold`}
        }
      }
    }
  }
`

const StyledImage = styled.div`
  ${tw`mt-8 relative w-full h-80 overflow-x-hidden md:overflow-visible`}

  & {
    :after {
      content: "";
      ${tw`block w-full h-72 absolute top-4 left-4 border-2 border-solid z-10 border-neutralLight rounded-lg`}
    }

    svg {
      ${tw`hidden text-neutralLight md:block md:absolute md:top-48 md:-right-12 `}
    }

    @media (min-width: 768px) {
      width: 25rem;
      height: 36rem;

      :after {
        content: "";
        height: 30rem;
        border-radius: 12rem;
        top: -2rem;
        left: 2rem;
        z-index: -10;
      }
    }

    .img {
      ${tw`w-full h-72 overflow-hidden`}
      @media(min-width: 768px) {
        height: 30rem;
        border-radius: 12rem;
      }

      picture,
      img {
        transform: scale(1.2);
        @media (min-width: 768px) {
          transition: all 0.25s var(--easing) !important;
          transform: scale(1);

          :hover,
          :focus {
            transform: scale(1.25);
          }
        }
      }
    }
  }
`

const About = () => {
  const data = useStaticQuery(graphql`
    query {
      avatar: file(
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "profile.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 500) {
            ...GatsbyImageSharpFluid_withWebp_tracedSVG
          }
        }
      }
    }
  `)

  const revealContainer = useRef(null)

  useEffect(() => {
    scrollReveal.reveal(revealContainer.current, srConfig())
  }, [])

  return (
    <StyledAbout id="about" ref={revealContainer}>
      <CustomHeading
        title="About Me"
        subTitle="Get to know me a little better"
      />
      <StyledContent>
        <StyledImage>
          <Img
            fluid={data.avatar.childImageSharp.fluid}
            alt="Avatar"
            className="img"
          />
          <Dots />
        </StyledImage>

        <div className="text-about">
          <div className="first">
            <p className="upper-text">Let me start with greetings!</p>

            <p>
              Yo! I’m Anubhav, as you already know. I’m a Software Design Engineer based in India, on a continuous quest for knowledge. 
              I’m passionate about innovation and love exploring immersive technologies like 
              <span tw="text-blue-400">
              AR/VR, Unity/Unreal game development, and large language models (LLMs). 
              </span>
              Recently, I’ve developed a keen interest in Quantum Science, 
              driven by curiosity to learn and uncover its possibilities.
              .
            </p>

            <p>
            In my free time, you’ll often find me watching anime, reading manga, or playing video games.
            I also enjoy tinkering with tech and building my own homelab setup.
            </p>
          </div>
          <div className="second">
            <h1>Skills</h1>
            <SkillTree />
          </div>
        </div>
      </StyledContent>
    </StyledAbout>
  )
}

export default About
